import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';

import Countdown from '../../../common/components/Countdown/Countdown';
import { DRAW_INTERVAL_MS, TICKET_PRICE, TOTAL_NUMBERS } from '../../../core/constants/lottery';
import {
    NUMBER_STATES,
    QUICK_PICK_COUNT,
    STORE_PARAMS,
} from '../../../core/constants/store';
import { pickAvailableNumbers } from '../../../core/lottery/inventory';
import { formatCount, formatCurrency } from '../../../core/utils/format';
import { useDraws } from '../../../global/contexts/lottery/draws';
import { useTickets } from '../../../global/contexts/lottery/tickets';
import { CartProvider, useCart } from '../contexts/cart';
import CheckoutForm from '../forms/checkout';
import {
    buildMatches,
    clampPage,
    pageCount,
    pageSlice,
    sanitizeQuery,
    toTicketNumber,
} from '../utils/numbers';
import StoreCart from './cart';
import NumberGrid from './grid';

import '../styles/store.css';

const FIRST_PAGE = 1;

const LEGEND = [
    { state: NUMBER_STATES.FREE, label: 'Disponible' },
    { state: NUMBER_STATES.SOLD, label: 'Vendido' },
    { state: NUMBER_STATES.MINE, label: 'Tuyo' },
];

function StoreLegend() {
    return (
        <ul className="store__legend">
            {LEGEND.map(entry => (
                <li className="store__legend-item" key={entry.state}>
                    <span className={`store__swatch store__swatch--${entry.state}`} />
                    {entry.label}
                </li>
            ))}
        </ul>
    );
}

function StoreWrapper() {
    const [params, setParams] = useSearchParams();
    const { isOwned, isPresold, isTaken, availableCount, presoldCount } = useTickets();
    const { msToNextDraw, nextDrawAt, advanceDraw } = useDraws();
    const cart = useCart();

    const [onlyAvailable, setOnlyAvailable] = useState(false);
    const [isCheckoutOpen, setCheckoutOpen] = useState(false);

    const query = sanitizeQuery(params.get(STORE_PARAMS.QUERY));
    const requestedPage = Number(params.get(STORE_PARAMS.PAGE)) || FIRST_PAGE;

    const matches = useMemo(
        () => buildMatches(query, onlyAvailable, isTaken),
        [query, onlyAvailable, isTaken],
    );

    const page = clampPage(requestedPage, matches.length);
    const totalPages = pageCount(matches.length);
    const visible = useMemo(() => pageSlice(matches, page), [matches, page]);

    // TSS vite/03 §"URL state" — the query and page survive a reload and a
    // back/forward, but they are replaced rather than pushed so typing four
    // digits doesn't leave four entries in the history stack.
    const writeParams = useCallback((changes) => {
        const next = new URLSearchParams(params);
        for (const [key, value] of Object.entries(changes)) {
            const isDefault = value === null
                || value === ''
                || (key === STORE_PARAMS.PAGE && Number(value) === FIRST_PAGE);
            if (isDefault) next.delete(key);
            else next.set(key, String(value));
        }
        setParams(next, { replace: true });
    }, [params, setParams]);

    const stateOf = useCallback((value) => {
        if (isOwned(value)) return NUMBER_STATES.MINE;
        if (isPresold(value)) return NUMBER_STATES.SOLD;
        return NUMBER_STATES.FREE;
    }, [isOwned, isPresold]);

    const handleQuickPick = (count) => {
        const picked = pickAvailableNumbers(
            Math.random,
            count,
            value => isTaken(value) || cart.isSelected(toTicketNumber(value)),
        );
        if (!picked.length) {
            toast.error('No quedan números disponibles');
            return;
        }
        cart.add(picked);
    };

    return (
        <section className="store section">
            <div className="section__head">
                <div>
                    <h1 className="section__title">Tienda de números</h1>
                    <p className="section__subtitle">
                        {formatCount(TOTAL_NUMBERS)} combinados del 0000 al 9999 ·
                        {' '}{formatCurrency(TICKET_PRICE)} cada uno ·
                        {' '}<strong>{formatCount(availableCount)}</strong> libres,
                        {' '}{formatCount(presoldCount)} ya vendidos.
                    </p>
                </div>
                <Countdown
                    compact
                    msRemaining={msToNextDraw}
                    intervalMs={DRAW_INTERVAL_MS}
                    nextAt={nextDrawAt}
                    onSimulate={advanceDraw}
                />
            </div>

            <div className="store__body">
                <div className="store__main">
                    <div className="store__toolbar panel">
                        <div className="store__search">
                            <span className="store__search-icon" aria-hidden="true">⌕</span>
                            <input
                                className="store__search-input"
                                value={query}
                                onChange={e => writeParams({
                                    [STORE_PARAMS.QUERY]: sanitizeQuery(e.target.value),
                                    [STORE_PARAMS.PAGE]: null,
                                })}
                                placeholder="Busca por dígitos: 7, 04, 123…"
                                inputMode="numeric"
                                aria-label="Buscar número por prefijo"
                            />
                            {query && (
                                <button
                                    type="button"
                                    className="store__search-clear"
                                    onClick={() => writeParams({ [STORE_PARAMS.QUERY]: null, [STORE_PARAMS.PAGE]: null })}
                                    aria-label="Limpiar búsqueda"
                                >
                                    ✕
                                </button>
                            )}
                        </div>

                        <label className="store__toggle">
                            <input
                                type="checkbox"
                                checked={onlyAvailable}
                                onChange={(e) => {
                                    setOnlyAvailable(e.target.checked);
                                    writeParams({ [STORE_PARAMS.PAGE]: null });
                                }}
                            />
                            Solo disponibles
                        </label>

                        <div className="store__quick">
                            <button
                                type="button"
                                className="button button--ghost button--sm"
                                onClick={() => handleQuickPick(1)}
                            >
                                🎲 Número sorpresa
                            </button>
                            <button
                                type="button"
                                className="button button--ghost button--sm"
                                onClick={() => handleQuickPick(QUICK_PICK_COUNT)}
                            >
                                ×{QUICK_PICK_COUNT}
                            </button>
                        </div>
                    </div>

                    <div className="store__meta">
                        <StoreLegend />
                        <span className="store__count">
                            {formatCount(matches.length)} {matches.length === 1 ? 'número' : 'números'}
                        </span>
                    </div>

                    <NumberGrid
                        values={visible}
                        stateOf={stateOf}
                        isSelected={cart.isSelected}
                        onToggle={cart.toggle}
                    />

                    {totalPages > 1 && (
                        <nav className="store__pager" aria-label="Paginación">
                            <button
                                type="button"
                                className="button button--ghost button--sm"
                                disabled={page <= 1}
                                onClick={() => writeParams({ [STORE_PARAMS.PAGE]: page - 1 })}
                            >
                                ← Anterior
                            </button>
                            <span className="store__pager-status">
                                Página {formatCount(page)} de {formatCount(totalPages)}
                            </span>
                            <button
                                type="button"
                                className="button button--ghost button--sm"
                                disabled={page >= totalPages}
                                onClick={() => writeParams({ [STORE_PARAMS.PAGE]: page + 1 })}
                            >
                                Siguiente →
                            </button>
                        </nav>
                    )}
                </div>

                <StoreCart playsAt={nextDrawAt} onCheckout={() => setCheckoutOpen(true)} />
            </div>

            {isCheckoutOpen && (
                <CheckoutForm
                    numbers={cart.selected}
                    playsAt={nextDrawAt}
                    onClose={() => setCheckoutOpen(false)}
                />
            )}
        </section>
    );
}

export default function Store() {
    return (
        <CartProvider>
            <StoreWrapper />
        </CartProvider>
    );
}
